'use client'

import { useRef, useState } from 'react'
import {
  Button,
  Col,
  Modal,
  ModalContent,
  ModalHeader,
  ModalTitle,
  Form,
  FormControl,
  FormField,
  FormItemWrapper,
  Icon,
  Input,
  Row,
  Alert,
} from '@repo/ui/components'
import { useTranslate } from '@repo/shared/hooks'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import Image from 'next/image'
import { EditProfileModalProps } from './edit-profile.types'

import { globalConfig } from '@repo/shared/utils'
import {
  useAgentSetProfile,
  useAgentSetUsername,
  useAgentUploadProfilePicture,
} from '@repo/services'
import { useErrorHandler } from '@repo/shared/hooks'
import { UploadedImage } from '@/components'
import {
  EditProfileFormValues,
  useEditProfileSchema,
} from '@repo/shared/schemas'
import { ImageCropper } from '@/components/image-cropper'

export const EditProfileModal = ({
  open,
  onOpenChange,
  initialData,
  profileImage,
}: EditProfileModalProps) => {
  const { t } = useTranslate()
  const { errorHandler } = useErrorHandler()
  const { schema: editProfileSchema } = useEditProfileSchema()

  const { mutate: setProfileMutation, isPending: isLoadingMutation } =
    useAgentSetProfile({
      invalidate: true,
    })
  const { mutate: setUsernameMutation, isPending: isLoadingUsernameMutation } =
    useAgentSetUsername({
      invalidate: true,
    })

  const {
    mutate: uploadProfilePhotoMutation,
    isPending: isUploadProfilePhotoPending,
  } = useAgentUploadProfilePicture()

  const fileInputRef = useRef<HTMLInputElement>(null)

  const [uploadedImage, setUploadedImage] = useState<UploadedImage | undefined>(
    profileImage
      ? {
          id: 0,
          url: `${globalConfig.baseImageUrl}${profileImage}`,
          file: null,
          name: '',
          size: 0,
          status: 'success',
        }
      : undefined,
  )

  const [cropperImage, setCropperImage] = useState<string | null>(null)
  const [isCropperOpen, setIsCropperOpen] = useState(false)

  const form = useForm<EditProfileFormValues>({
    resolver: zodResolver(editProfileSchema),
    defaultValues: {
      fullName: initialData?.fullName || '',
      username: initialData?.username || '',
      agencyName: initialData?.agencyName || '',
      agencyAddress: initialData?.agencyAddress || '',
      instagram: initialData?.instagram || '',
      telegram: initialData?.telegram || '',
      whatsapp: initialData?.whatsapp || '',
    },
  })

  const handleProfileImageUpload = (
    file: File,
    callback: (success: boolean) => void,
  ) => {
    const formData = new FormData()
    formData.append('file', file)

    uploadProfilePhotoMutation(formData, {
      onSuccess: () => {
        callback(true)
      },
      onError: (error) => {
        errorHandler(error, {
          type: 'both',
        })
        callback(false)
      },
    })
  }
  const handleUsernameSubmit = (data: EditProfileFormValues) => {
    setUsernameMutation(
      {
        username: data.username,
      },
      {
        onSuccess: () => {
          onOpenChange(false)
        },

        onError: (error) => {
          errorHandler(error, {
            type: 'both',
          })
        },
      },
    )
  }

  const handleSubmit = (data: EditProfileFormValues) => {
    if (
      uploadedImage &&
      uploadedImage.file &&
      uploadedImage.status === 'pending'
    ) {
      handleProfileImageUpload(uploadedImage.file!, (success) => {
        if (success) {
          setProfileMutation(
            {
              displayName: data.fullName,
              instagram: data.instagram,
              telegram: data.telegram,
              whatsapp: data.whatsapp,
              info: {
                agencyName: data.agencyName,
                agencyAddress: data.agencyAddress,
              },
            },
            {
              onSuccess: () => {
                if (data.username) {
                  handleUsernameSubmit(data)
                } else {
                  onOpenChange(false)
                }
              },
              onError: (error) => {
                errorHandler(error, {
                  type: 'both',
                })
              },
            },
          )
        }
      })
    } else {
      setProfileMutation(
        {
          displayName: data.fullName,
          instagram: data.instagram,
          telegram: data.telegram,
          whatsapp: data.whatsapp,
          info: {
            agencyName: data.agencyName,
            agencyAddress: data.agencyAddress,
          },
        },
        {
          onSuccess: () => {
            if (data.username) {
              handleUsernameSubmit(data)
            } else {
              onOpenChange(false)
            }
          },
          onError: (error) => {
            errorHandler(error, {
              type: 'both',
            })
          },
        },
      )
    }
  }

  const handleImageUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0]
    if (file) {
      const imageUrl = URL.createObjectURL(file)
      setCropperImage(imageUrl)
      setIsCropperOpen(true)
    }
    // Reset input value to allow selecting the same file again
    if (event.target) {
      event.target.value = ''
    }
  }

  const handleImageDelete = () => {
    setUploadedImage(undefined)
  }

  const handleNewImageClick = () => {
    fileInputRef.current?.click()
  }

  const handleCropComplete = (croppedFile: File) => {
    setUploadedImage({
      id: Number(Math.random().toString(10).substring(2, 15)),
      url: URL.createObjectURL(croppedFile),
      file: croppedFile,
      name: croppedFile.name,
      size: croppedFile.size,
      status: 'pending',
    })
    setCropperImage(null)
    setIsCropperOpen(false)
  }

  const handleCropperClose = () => {
    setCropperImage(null)
    setIsCropperOpen(false)
  }

  const currentProfileImage = uploadedImage
    ? uploadedImage.url
    : '/images/placeholder.webp'

  return (
    <Modal open={open} onOpenChange={onOpenChange}>
      <ModalContent>
        <ModalHeader>
          <ModalTitle className='text-center'>
            {t('editProfile_title')}
          </ModalTitle>
        </ModalHeader>

        <Col className='p-lg max-h-[600px] overflow-y-auto'>
          <Form {...form}>
            <form
              className='space-y-lg'
              onSubmit={form.handleSubmit(handleSubmit)}
            >
              {/* Profile Image Section */}
              <Row className='items-center gap-md'>
                <Row className='gap-xs'>
                  <Button
                    type='button'
                    variant='fill'
                    size='sm'
                    color='base'
                    startIcon={<Icon name='Plus' size='sm' />}
                    onClick={handleNewImageClick}
                  >
                    {t('editProfile_newImage')}
                  </Button>
                  {uploadedImage && (
                    <Button
                      type='button'
                      variant='ghost'
                      size='sm'
                      color='negative'
                      startIcon={<Icon name='TrashBinLine' size='sm' />}
                      onClick={handleImageDelete}
                      className='border border-negative-surface-100 text-negative-surface-100 hover:bg-negative-surface-20'
                    >
                      {t('editProfile_deleteImage')}
                    </Button>
                  )}
                  <input
                    ref={fileInputRef}
                    type='file'
                    accept='image/*'
                    onChange={handleImageUpload}
                    className='hidden'
                  />
                </Row>
                <div className='rounded-full bg-neutral-surface-20 overflow-hidden'>
                  <Image
                    src={currentProfileImage}
                    width={64}
                    height={64}
                    alt='Profile'
                    className='object-cover'
                  />
                </div>
              </Row>

              {/* Form Fields */}
              <FormField
                control={form.control}
                name='fullName'
                render={({ field }) => (
                  <FormItemWrapper title={t('editProfile_fullName')}>
                    <FormControl>
                      <Input
                        placeholder={t('editProfile_fullName_placeholder')}
                        {...field}
                      />
                    </FormControl>
                  </FormItemWrapper>
                )}
              />

              <FormField
                control={form.control}
                name='username'
                render={({ field }) => (
                  <FormItemWrapper title={t('editProfile_username')}>
                    <Input
                      placeholder={t('editProfile_username_placeholder')}
                      {...field}
                    />
                  </FormItemWrapper>
                )}
              />

              <FormField
                control={form.control}
                name='agencyName'
                render={({ field }) => (
                  <FormItemWrapper title={t('editProfile_agencyName')}>
                    <Input
                      placeholder={t('editProfile_agencyName_placeholder')}
                      {...field}
                    />
                  </FormItemWrapper>
                )}
              />

              <FormField
                control={form.control}
                name='agencyAddress'
                render={({ field }) => (
                  <FormItemWrapper title={t('editProfile_agencyAddress')}>
                    <Input
                      placeholder={t('editProfile_agencyAddress_placeholder')}
                      {...field}
                    />
                  </FormItemWrapper>
                )}
              />

              <FormField
                control={form.control}
                name='instagram'
                render={({ field }) => (
                  <FormItemWrapper title={t('editProfile_instagram')}>
                    <Input
                      placeholder={t('editProfile_instagram_placeholder')}
                      startIcon={<Icon name='Instagram' size='sm' />}
                      {...field}
                    />
                  </FormItemWrapper>
                )}
              />

              <FormField
                control={form.control}
                name='telegram'
                render={({ field }) => (
                  <FormItemWrapper title={t('editProfile_telegram')}>
                    <Input
                      placeholder={t('editProfile_telegram_placeholder')}
                      startIcon={<Icon name='TelegramFill' size='sm' />}
                      {...field}
                    />
                  </FormItemWrapper>
                )}
              />

              <FormField
                control={form.control}
                name='whatsapp'
                render={({ field }) => (
                  <FormItemWrapper title={t('editProfile_whatsapp')}>
                    <Input
                      placeholder={t('editProfile_whatsapp_placeholder')}
                      startIcon={<Icon name='WhatsappFill' size='sm' />}
                      {...field}
                    />
                  </FormItemWrapper>
                )}
              />

              <Alert
                color='alert'
                caption={t('editProfile_warning_message')}
                icon={<Icon name='AlertTriangle' />}
              />

              {/* Submit Button */}
              <Button
                type='submit'
                variant='fill'
                size='lg'
                color='base'
                width='full'
                className='mt-lg'
                loading={
                  isLoadingMutation ||
                  isUploadProfilePhotoPending ||
                  isLoadingUsernameMutation
                }
              >
                {t('editProfile_saveChanges')}
              </Button>
            </form>
          </Form>
        </Col>
      </ModalContent>

      <ImageCropper
        open={isCropperOpen}
        onClose={handleCropperClose}
        imageSrc={cropperImage}
        onCropComplete={handleCropComplete}
        aspect={1}
        cropShape='round'
      />
    </Modal>
  )
}
